/**
 * Переходы режимов «Developer Pack» и «Email and Web Pack».
 *
 * Режимы собирают занятие сами, но mastery и XP считаются теми же функциями,
 * что и после обычной тренировки: результат для пользователя один и тот же, а
 * два разных пути его подсчёта разошлись бы (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  PACK_LAYOUT,
  PACK_MODES,
  applyPackOutcomes,
  buildPacksPlan,
  collectPackOutcomes,
  hasPack,
  type PackMode,
  type PacksPlan,
  type PacksSnapshot,
} from '@type2x/feature-packs';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/**
 * Снимок навыка для режимов пакетов.
 *
 * Раскладка в снимок не входит: материал пакетов существует только на
 * латинской, и профиль на неё не влияет (см. PACK_LAYOUT).
 */
export function packsSnapshot(workspace: Workspace): PacksSnapshot {
  const profile = activeProfile(workspace);
  return { language: profile.language, stats: profile.stats };
}

/** Режимы пакетов, которым ещё есть чему учить. */
export function availablePackModes(workspace: Workspace | null): PackMode[] {
  if (workspace === null) return [];

  const snapshot = packsSnapshot(workspace);
  return PACK_MODES.filter((mode) => hasPack(snapshot, mode));
}

/** Экран режима со свежим занятием. */
export function packsScreen(runtime: Runtime, workspace: Workspace, mode: PackMode): AppScreen {
  return {
    name: 'packs',
    plan: buildPacksPlan(
      packsSnapshot(workspace),
      mode,
      `${runtime.installationId}:${mode}:${runtime.clock.epochMs()}`,
    ),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Завершает занятие: обновляет сочетания, начисляет XP и собирает итог. */
export function finishPacks(
  runtime: Runtime,
  workspace: Workspace,
  plan: PacksPlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectPackOutcomes(plan, session.keystrokeEvents, session.typingErrors);
  const statsAfter = applyPackOutcomes(plan.units, profile.stats, outcomes, CONFIG, now);

  // Сочетание могло встретиться впервые: его единица создаётся вместе с итогом.
  let next = upsertMastery(workspace, plan.units, statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'packs', mode: plan.mode }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: plan.mode,
      language: profile.language,
      // Раскладка итога — та, на которой шло занятие: прогресс копится
      // отдельно для каждой раскладки (ТЗ §8.1).
      layoutId: PACK_LAYOUT,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.typingErrors,
      isControlled: false,
    }),
  };
}
